import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore'
import { db } from './config'
import { getCurrentUser } from './auth'
import { logger } from '@/utils/logger'
import { errorHandler } from '@/utils/errorHandler'

export interface EmailTemplate {
  id: string
  name: string
  type: 'inquiry' | 'confirmation' | 'reply' | 'admin-notification' // 'inquiry' = Anfrage an Admin, 'confirmation' = Bestätigung an Nutzer, 'reply' = Antwort an Kunden, 'admin-notification' = Kurze Info an private E-Mail
  subject: string
  htmlContent: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
  updatedBy?: string
}

// Standard-Templates mit modernen HTML-Templates und Dark Mode Support

// Template 1: Admin-Notification - Einfache Benachrichtigung an private E-Mail
const DEFAULT_ADMIN_NOTIFICATION_TEMPLATE_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Neue Kontaktanfrage erhalten</title>
</head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333333; background-color: #f5f5f5; margin: 0; padding: 20px;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
    
    <!-- Header -->
    <div style="background-color: #2563eb; padding: 30px; text-align: center;">
      <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: bold;">Neue Kontaktanfrage erhalten</h1>
    </div>
    
    <!-- Content -->
    <div style="padding: 30px;">
      
      <!-- Name -->
      <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; color: #666666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Name</div>
        <div style="font-size: 16px; color: #111827;">{{name}}</div>
      </div>
      
      <!-- E-Mail -->
      <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; color: #666666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">E-Mail</div>
        <div style="font-size: 16px; color: #111827;"><a href="mailto:{{email}}" style="color: #2563eb; text-decoration: none;">{{email}}</a></div>
      </div>
      
      <!-- Interessensgebiet (optional) -->
      {{#if interestLabel}}
      <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; color: #666666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Interessensgebiet</div>
        <div style="font-size: 16px; color: #111827;">{{interestLabel}}</div>
      </div>
      {{/if}}
      
      <!-- Projekttyp (optional) -->
      {{#if projectTypeLabel}}
      <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; color: #666666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Projekttyp</div>
        <div style="font-size: 16px; color: #111827;">{{projectTypeLabel}}</div>
      </div>
      {{/if}}
      
      <!-- Budget (optional) -->
      {{#if budgetLabel}}
      <div style="margin-bottom: 20px;">
        <div style="font-weight: bold; color: #666666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Budget</div>
        <div style="font-size: 16px; color: #111827;">{{budgetLabel}}</div>
      </div>
      {{/if}}
      
      <!-- Nachricht -->
      <div style="margin-bottom: 25px;">
        <div style="font-weight: bold; color: #666666; font-size: 12px; text-transform: uppercase; margin-bottom: 5px;">Nachricht</div>
        <div style="background-color: #f9fafb; padding: 15px; border-radius: 6px; border-left: 3px solid #2563eb; white-space: pre-wrap; font-size: 15px; color: #374151;">{{message}}</div>
      </div>
      
      <!-- Hinweis -->
      <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
        <p style="margin: 0; font-size: 14px; color: #92400e;">
          <strong>Bitte prüfe dein Strato Postfach zum Beantworten der Anfrage.</strong>
        </p>
      </div>
      
    </div>
    
    <!-- Footer -->
    <div style="background-color: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb;">
      <p style="margin: 0; font-size: 12px; color: #6b7280;">Automatische E-Mail - Reitsberger.de</p>
      <p style="margin: 5px 0 0; font-size: 12px; color: #9ca3af;">Gesendet am: {{date}}</p>
    </div>
    
  </div>
</body>
</html>`

// Template 2: Inquiry - Ausführliche Anfrage-Details an Admin (Strato E-Mail)
const DEFAULT_INQUIRY_TEMPLATE_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light dark">
  <meta name="supported-color-schemes" content="light dark">
  <title>Neue Kontaktanfrage</title>
  <style>
    @media (prefers-color-scheme: dark) {
      .email-container {
        background-color: #1f2937 !important;
        color: #f3f4f6 !important;
      }
      .email-body {
        background-color: #111827 !important;
        border-color: #374151 !important;
      }
      .email-card {
        background-color: #1f2937 !important;
        border-color: #374151 !important;
      }
      .email-text-primary {
        color: #f9fafb !important;
      }
      .email-text-secondary {
        color: #d1d5db !important;
      }
      .email-text-muted {
        color: #9ca3af !important;
      }
      .email-border {
        border-color: #374151 !important;
      }
      .email-link {
        color: #60a5fa !important;
      }
      .checkmark-circle {
        background-color: rgba(255,255,255,0.2) !important;
        border: 3px solid rgba(255,255,255,0.3) !important;
      }
      .checkmark-icon {
        color: white !important;
        font-weight: bold !important;
      }
      .email-info-box {
        background-color: #1e3a5f !important;
        border-color: #3b82f6 !important;
      }
      .email-info-text {
        color: #93c5fd !important;
      }
      .email-message-box {
        background-color: #1f2937 !important;
      }
    }
  </style>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #111827; background-color: #ffffff; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); padding: 40px; text-align: center; border-radius: 10px 10px 0 0;">
    <div class="checkmark-circle" style="background-color: rgba(255,255,255,0.95); width: 100px; height: 100px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border: 3px solid rgba(255,255,255,0.5);">
      <span class="checkmark-icon" style="font-size: 56px; color: #3b82f6; font-weight: bold; line-height: 1; display: block;">📧</span>
    </div>
    <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Neue Kontaktanfrage</h1>
    <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0; font-size: 16px;">Sie haben eine neue Nachricht erhalten</p>
  </div>
  
  <div class="email-container" style="background-color: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e5e7eb; border-top: none;">
    <div class="email-card" style="background-color: #ffffff; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <p class="email-text-secondary" style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        Sie haben eine neue Kontaktanfrage von <strong class="email-text-primary" style="color: #111827;">{{name}}</strong> erhalten.
      </p>
    </div>
    
    <div class="email-card" style="background-color: #ffffff; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <h2 class="email-text-primary" style="color: #111827; font-size: 18px; font-weight: 600; margin: 0 0 15px;">Kontaktdaten:</h2>
      
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Name</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;">{{name}}</div>
      </div>
      
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">E-Mail</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;"><a href="mailto:{{email}}" class="email-link" style="color: #3b82f6; text-decoration: none;">{{email}}</a></div>
      </div>
      
      {{#if interestLabel}}
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Interessensgebiet</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;">{{interestLabel}}</div>
      </div>
      {{/if}}
      
      {{#if projectTypeLabel}}
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Projekttyp</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;">{{projectTypeLabel}}</div>
      </div>
      {{/if}}
      
      {{#if budgetLabel}}
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Budget</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;">{{budgetLabel}}</div>
      </div>
      {{/if}}
      
      <div style="margin-top: 15px;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Nachricht</div>
        <div class="email-message-box email-text-secondary" style="color: #374151; line-height: 1.8; white-space: pre-wrap; background-color: #f9fafb; padding: 15px; border-radius: 6px;">{{message}}</div>
      </div>
    </div>
    
    <div class="email-info-box" style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
      <p class="email-info-text" style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.6;">
        <strong>Nächste Schritte:</strong> Bitte antworten Sie dem Kunden innerhalb von 24 Stunden. Die Kontaktdaten finden Sie oben in dieser E-Mail.
      </p>
    </div>
    
    <div class="email-border email-text-muted" style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
      <p style="margin: 0;">Gesendet am: {{date}}</p>
    </div>
  </div>
</body>
</html>`

const DEFAULT_CONFIRMATION_TEMPLATE_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light dark">
  <meta name="supported-color-schemes" content="light dark">
  <title>Vielen Dank für Ihre Anfrage</title>
  <style>
    @media (prefers-color-scheme: dark) {
      .email-container {
        background-color: #1f2937 !important;
        color: #f3f4f6 !important;
      }
      .email-body {
        background-color: #111827 !important;
        border-color: #374151 !important;
      }
      .email-card {
        background-color: #1f2937 !important;
        border-color: #374151 !important;
      }
      .email-text-primary {
        color: #f9fafb !important;
      }
      .email-text-secondary {
        color: #d1d5db !important;
      }
      .email-text-muted {
        color: #9ca3af !important;
      }
      .email-border {
        border-color: #374151 !important;
      }
      .email-info-box {
        background-color: #1e3a5f !important;
        border-color: #3b82f6 !important;
      }
      .email-info-text {
        color: #93c5fd !important;
      }
      .email-message-box {
        background-color: #1f2937 !important;
      }
      .checkmark-circle {
        background-color: rgba(255,255,255,0.2) !important;
        border: 3px solid rgba(255,255,255,0.3) !important;
      }
      .checkmark-icon {
        color: white !important;
        font-weight: bold !important;
      }
    }
  </style>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #111827; background-color: #ffffff; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 40px; text-align: center; border-radius: 10px 10px 0 0;">
    <div class="checkmark-circle" style="background-color: rgba(255,255,255,0.95); width: 100px; height: 100px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border: 3px solid rgba(255,255,255,0.5);">
      <span class="checkmark-icon" style="font-size: 56px; color: #10b981; font-weight: bold; line-height: 1; display: block;">✓</span>
    </div>
    <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Vielen Dank!</h1>
    <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0; font-size: 16px;">Ihre Anfrage wurde erfolgreich übermittelt</p>
  </div>
  
  <div class="email-container" style="background-color: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e5e7eb; border-top: none;">
    <div class="email-card" style="background-color: #ffffff; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <p class="email-text-secondary" style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        Hallo <strong class="email-text-primary" style="color: #111827;">{{name}}</strong>,
      </p>
      <p class="email-text-secondary" style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        vielen Dank für Ihre Kontaktanfrage! Ich habe Ihre Nachricht erhalten und werde mich schnellstmöglich bei Ihnen melden.
      </p>
    </div>
    
    <div class="email-card" style="background-color: #ffffff; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <h2 class="email-text-primary" style="color: #111827; font-size: 18px; font-weight: 600; margin: 0 0 15px;">Ihre Anfrage im Überblick:</h2>
      
      {{#if interestLabel}}
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Interessensgebiet</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;">{{interestLabel}}</div>
      </div>
      {{/if}}
      
      {{#if projectTypeLabel}}
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Projekttyp</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;">{{projectTypeLabel}}</div>
      </div>
      {{/if}}
      
      {{#if budgetLabel}}
      <div class="email-border" style="margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Budget</div>
        <div class="email-text-primary" style="font-size: 16px; color: #111827;">{{budgetLabel}}</div>
      </div>
      {{/if}}
      
      <div style="margin-top: 15px;">
        <div class="email-text-muted" style="font-weight: 600; color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;">Ihre Nachricht</div>
        <div class="email-message-box email-text-secondary" style="color: #374151; line-height: 1.8; white-space: pre-wrap; background-color: #f9fafb; padding: 15px; border-radius: 6px;">{{message}}</div>
      </div>
    </div>
    
    <div class="email-info-box" style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
      <p class="email-info-text" style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.6;">
        <strong>Nächste Schritte:</strong> Ich werde Ihre Anfrage prüfen und mich innerhalb von 24 Stunden bei Ihnen melden. Bei dringenden Anliegen können Sie mich auch direkt kontaktieren.
      </p>
    </div>
    
    <div class="email-border email-text-muted" style="text-align: center; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;">
      <p style="margin: 0 0 10px;">Mit freundlichen Grüßen</p>
      <p class="email-text-primary" style="margin: 0; font-weight: 600; color: #111827;">MAXIMILIAN REITSBERGER</p>
      <p style="margin: 5px 0 0; font-size: 12px;">Webentwickler mit QA-Expertise</p>
    </div>
  </div>
</body>
</html>`

const DEFAULT_ADMIN_NOTIFICATION_TEMPLATE: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  name: 'Admin - Information (Private E-Mail)',
  type: 'admin-notification',
  subject: 'Neue Kontaktanfrage von {{name}}',
  htmlContent: DEFAULT_ADMIN_NOTIFICATION_TEMPLATE_HTML,
  isActive: true
}

const DEFAULT_INQUIRY_TEMPLATE: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  name: 'Anfrage-Template (Admin E-Mail)',
  type: 'inquiry',
  subject: 'Neue Kontaktanfrage von {{name}}',
  htmlContent: DEFAULT_INQUIRY_TEMPLATE_HTML,
  isActive: true
}

const DEFAULT_CONFIRMATION_TEMPLATE: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  name: 'Bestätigungs-Template (Standard)',
  type: 'confirmation',
  subject: 'Vielen Dank für Ihre Anfrage',
  htmlContent: DEFAULT_CONFIRMATION_TEMPLATE_HTML,
  isActive: true
}

const DEFAULT_REPLY_TEMPLATE_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light dark">
  <meta name="supported-color-schemes" content="light dark">
  <title>Antwort auf Ihre Anfrage</title>
  <style>
    @media (prefers-color-scheme: dark) {
      .email-container {
        background-color: #1f2937 !important;
        color: #f3f4f6 !important;
      }
      .email-body {
        background-color: #111827 !important;
        border-color: #374151 !important;
      }
      .email-card {
        background-color: #1f2937 !important;
        border-color: #374151 !important;
      }
      .email-text-primary {
        color: #f9fafb !important;
      }
      .email-text-secondary {
        color: #d1d5db !important;
      }
      .email-text-muted {
        color: #9ca3af !important;
      }
      .email-border {
        border-color: #374151 !important;
      }
      .email-link {
        color: #60a5fa !important;
      }
      .checkmark-circle {
        background-color: rgba(255,255,255,0.2) !important;
        border: 3px solid rgba(255,255,255,0.3) !important;
      }
      .checkmark-icon {
        color: white !important;
        font-weight: bold !important;
      }
      .email-info-box {
        background-color: #1e3a5f !important;
        border-color: #3b82f6 !important;
      }
      .email-info-text {
        color: #93c5fd !important;
      }
      .email-message-box {
        background-color: #1f2937 !important;
      }
      .email-signature {
        background-color: #1f2937 !important;
        border-color: #374151 !important;
      }
    }
  </style>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #111827; background-color: #ffffff; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); padding: 40px; text-align: center; border-radius: 10px 10px 0 0;">
    <div class="checkmark-circle" style="background-color: rgba(255,255,255,0.95); width: 100px; height: 100px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border: 3px solid rgba(255,255,255,0.5);">
      <span class="checkmark-icon" style="font-size: 56px; color: #6366f1; font-weight: bold; line-height: 1; display: block;">✉️</span>
    </div>
    <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 700;">Antwort auf Ihre Anfrage</h1>
    <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0; font-size: 16px;">Vielen Dank für Ihr Interesse</p>
  </div>
  
  <div class="email-container" style="background-color: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e5e7eb; border-top: none;">
    <div class="email-card" style="background-color: #ffffff; padding: 25px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <p class="email-text-secondary" style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        Hallo <strong class="email-text-primary" style="color: #111827;">{{name}}</strong>,
      </p>
      <p class="email-text-secondary" style="color: #374151; font-size: 16px; line-height: 1.8; margin: 0 0 20px;">
        vielen Dank für Ihre Kontaktanfrage vom {{date}}. Ich habe Ihre Nachricht erhalten und möchte Ihnen gerne antworten.
      </p>
    </div>
    
    <div class="email-card" style="background-color: #ffffff; padding: 20px; border-radius: 8px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); border: 1px solid #e5e7eb;">
      <div class="email-message-box email-text-secondary" style="color: #374151; line-height: 1.8; white-space: pre-wrap; background-color: #f9fafb; padding: 20px; border-radius: 6px; font-size: 16px;">{{message}}</div>
    </div>
    
    {{#if nextSteps}}
    <div class="email-info-box" style="background-color: #eff6ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 6px; margin-bottom: 20px;">
      <p class="email-info-text" style="color: #1e40af; font-size: 14px; margin: 0; line-height: 1.6;">
        <strong>Nächste Schritte:</strong> {{nextSteps}}
      </p>
    </div>
    {{/if}}
    
    <div class="email-signature email-border email-text-muted" style="text-align: center; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;">
      <p style="margin: 0 0 10px;">Mit freundlichen Grüßen</p>
      <p class="email-text-primary" style="margin: 0; font-weight: 600; color: #111827;">MAXIMILIAN REITSBERGER</p>
      <p style="margin: 5px 0 0; font-size: 12px;">Webentwickler mit QA-Expertise</p>
      <p style="margin: 10px 0 0; font-size: 12px;">
        <a href="mailto:kontakt@reitsberger.de" class="email-link" style="color: #3b82f6; text-decoration: none;">kontakt@reitsberger.de</a>
      </p>
    </div>
  </div>
</body>
</html>`

const DEFAULT_REPLY_TEMPLATE: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  name: 'Antwort-Template (Standard)',
  type: 'reply',
  subject: 'Antwort auf Ihre Anfrage',
  htmlContent: DEFAULT_REPLY_TEMPLATE_HTML,
  isActive: true
}

/**
 * Lade alle Email-Templates
 */
export const getEmailTemplates = async (): Promise<EmailTemplate[]> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const templatesRef = collection(db, 'emailTemplates')
    const snapshot = await getDocs(templatesRef)
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as EmailTemplate[]
  } catch (error) {
    errorHandler.handle(error, 'getEmailTemplates')
    throw error
  }
}

/**
 * Lade ein spezifisches Template
 */
export const getEmailTemplate = async (id: string): Promise<EmailTemplate | null> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const templateRef = doc(db, 'emailTemplates', id)
    const templateSnap = await getDoc(templateRef)
    
    if (!templateSnap.exists()) {
      return null
    }
    
    return {
      id: templateSnap.id,
      ...templateSnap.data(),
      createdAt: templateSnap.data().createdAt?.toDate() || new Date(),
      updatedAt: templateSnap.data().updatedAt?.toDate() || new Date()
    } as EmailTemplate
  } catch (error) {
    errorHandler.handle(error, 'getEmailTemplate')
    throw error
  }
}

/**
 * Lade das aktive Template für einen bestimmten Typ
 */
export const getActiveEmailTemplate = async (type: 'inquiry' | 'confirmation' | 'reply' | 'admin-notification'): Promise<EmailTemplate | null> => {
  try {
    const templates = await getEmailTemplates()
    const activeTemplate = templates.find(t => t.type === type && t.isActive)
    
    if (activeTemplate) {
      return activeTemplate
    }
    
    // Fallback: Erstelle Standard-Template falls keines existiert
    return null
  } catch (error) {
    logger.error('Fehler beim Laden des aktiven Email-Templates', error)
    return null
  }
}

/**
 * Erstelle oder aktualisiere ein Email-Template
 */
export const saveEmailTemplate = async (template: Partial<EmailTemplate> & { name: string; type: 'inquiry' | 'confirmation' | 'reply' | 'admin-notification' }): Promise<string> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }
    
    const templateData: Record<string, any> = {
      ...template,
      updatedAt: Timestamp.now(),
      updatedBy: user.uid
    }
    
    // Wenn neues Template, setze createdAt
    if (!template.id) {
      templateData.createdAt = Timestamp.now()
      const templatesRef = collection(db, 'emailTemplates')
      const newDocRef = doc(templatesRef)
      await setDoc(newDocRef, templateData)
      return newDocRef.id
    } else {
      // Update bestehendes Template
      const templateRef = doc(db, 'emailTemplates', template.id)
      await updateDoc(templateRef, templateData)
      return template.id
    }
  } catch (error) {
    errorHandler.handle(error, 'saveEmailTemplate')
    throw error
  }
}

/**
 * Erstelle Standard-Templates (einmalig)
 * Erstellt nur fehlende Templates, nicht alle neu
 */
export const createDefaultTemplates = async (): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const user = getCurrentUser()
    if (!user) {
      throw new Error('Nicht authentifiziert')
    }
    
    const templates = await getEmailTemplates()
    const existingTypes = new Set(templates.map(t => t.type))
    const now = Timestamp.now()
    
    // Erstelle Admin Notification Template falls nicht vorhanden
    if (!existingTypes.has('admin-notification')) {
      const adminNotificationRef = doc(collection(db, 'emailTemplates'))
      await setDoc(adminNotificationRef, {
        ...DEFAULT_ADMIN_NOTIFICATION_TEMPLATE,
        createdAt: now,
        updatedAt: now,
        updatedBy: user.uid
      })
    }
    
    // Erstelle Inquiry Template falls nicht vorhanden
    if (!existingTypes.has('inquiry')) {
      const inquiryRef = doc(collection(db, 'emailTemplates'))
      await setDoc(inquiryRef, {
        ...DEFAULT_INQUIRY_TEMPLATE,
        createdAt: now,
        updatedAt: now,
        updatedBy: user.uid
      })
    }
    
    // Erstelle Confirmation Template falls nicht vorhanden
    if (!existingTypes.has('confirmation')) {
      const confirmationRef = doc(collection(db, 'emailTemplates'))
      await setDoc(confirmationRef, {
        ...DEFAULT_CONFIRMATION_TEMPLATE,
        createdAt: now,
        updatedAt: now,
        updatedBy: user.uid
      })
    }
    
    // Erstelle Reply Template falls nicht vorhanden
    if (!existingTypes.has('reply')) {
      const replyRef = doc(collection(db, 'emailTemplates'))
      await setDoc(replyRef, {
        ...DEFAULT_REPLY_TEMPLATE,
        createdAt: now,
        updatedAt: now,
        updatedBy: user.uid
      })
    }
  } catch (error) {
    errorHandler.handle(error, 'createDefaultTemplates')
    throw error
  }
}
